import classNames from "classnames";
import React, { ReactElement, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Button } from "@/components/Button";
import { IEditableSectionRenderProps } from "@/components/EditableSection";
import { Group } from "@/components/Group";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { getProfileRole } from "@/data/consultant-profiles";
import { formatFullName } from "@/data/persons";
import { refresh } from "@/data/session";
import { assignEmailToConsultantProfile } from "@/data/users";
import { Flex } from "@/eworkitComponents";
import { RouteButton } from "@/infrastructure/Route";
import { IConsultantProfileSectionProps } from "@/views/components/ConsultantProfile/Sections/types";
import { useToasts } from "@/views/components/Toast/useToasts";
import { AvatarSection } from "@/views/ConsultantProfiles/Detail/AvatarSection";
import { router } from "@/views/router";
import { EmailHandlingModal } from "@/views/Settings/UserSettings/UserAccount/UserName/EmailHandlingModal";

import { GeneralSectionEdit } from "./GeneralSectionEdit";
import { InfoSection } from "./InfoSection";

import "./_general-section.scss";

interface Props extends IConsultantProfileSectionProps, IEditableSectionRenderProps {
  isLink?: boolean;
  sideSection?: ReactElement | null;
  afterUpdate?: () => void;
}

export const GeneralSection: React.FC<Props> = React.memo(
  ({ editableProps, isLink, record, onUpdate, sideSection, afterUpdate }) => {
    const fullName = formatFullName(record);
    const baseClass = "general-section";
    const User = useContext(UserContext);
    const { addToast } = useToasts();

    const [isEditEmailModalOpen, setEditEmailModalOpen] = useState(false);
    const isOwner = User.hasConsultantProfile(record.id);

    const { t } = useTranslation("session");

    const isSmallScreen = useMedia(MEDIA_SIZE_SM);

    const handleEmailUpdate = useCallback(async () => {
      const user = await refresh();
      User.updateContextUser(user);
      afterUpdate?.();
      setEditEmailModalOpen(false);

      addToast({ text: t("USERS.SETTINGS.USER_ACCOUNT.SUCCESS_MESSAGE"), type: "success" });
    }, []);

    return (
      <Flex direction={isSmallScreen ? "column" : "row"} innerMargin={isSmallScreen && "md"}>
        <Flex width={isSmallScreen ? 12 : 3}>
          <AvatarSection record={record} onUpdate={onUpdate} />
        </Flex>

        <Flex direction="column" width={isSmallScreen ? 12 : 9} innerMargin="md">
          <Flex direction="column" innerMargin="xs">
            <Flex>
              <Flex grow={1} align="center">
                <Header
                  weight="bold"
                  size="lg"
                  text={fullName}
                  className={classNames(
                    "-word-break-word",
                    `${baseClass}-header`,
                    editableProps.editMode ? "edit" : "read",
                  )}
                  hjSuppress
                />
                {isLink && (
                  <RouteButton
                    variant="clean"
                    path={
                      router.get("consultant-profiles.detail", {
                        consultantProfileId: record.parent ? record.parent.id : record.id,
                      }).path
                    }>
                    <Icon size="md" color="grey3" icon="launch" />
                  </RouteButton>
                )}
              </Flex>
              <Flex grow={0}>{sideSection}</Flex>
            </Flex>

            <Text size="md">
              {t(`CONSULTANT_PROFILES.DETAIL.PROFILE_ROLE.${getProfileRole(record.companyRole, record.requestToJoin)}`)}
            </Text>
          </Flex>

          {editableProps.editMode ? (
            <GeneralSectionEdit
              source={record}
              initialValues={{
                position: { name: record.position.name },
                location: record.location,
                phone: record.phone,
                availability: record.availability,
                availabilityDate: record.availabilityDate,
                workPreferences: record.workPreferences,
                email: record.email,
                firstName: record.firstName,
                lastName: record.lastName,
              }}
              editableProps={editableProps}
              onUpdate={onUpdate}
              onCancel={editableProps.handleCancelClick}
            />
          ) : (
            <InfoSection record={record} />
          )}
          <Flex border={[true, false, false, false]} padding={["md", false, false, false]} position="relative">
            <Group
              label={t("CONSULTANT_PROFILES.DETAIL.EMAIL.LABEL")}
              labelWidth={4}
              fieldWidth={8}
              direction={isSmallScreen ? "column" : "row"}
              align={record.pendingEmail || isSmallScreen ? "flex-start" : "center"}>
              <Flex
                direction="column"
                innerMargin="sm"
                padding={record.pendingEmail ? ["xs", false, false, false] : [false]}>
                <Flex direction="column">
                  <Text
                    size="md"
                    wordBreak="all"
                    color={record.status === "PENDING" || record.status === "NOT_INVITED" ? "grey1" : "black"}>
                    {record.email}
                  </Text>
                  {record.status === "PENDING" && (
                    <Text size="md" color="grey1">
                      {t("CONSULTANT_PROFILES.DETAIL.EMAIL.WAITING_FOR_CONFIRMATION")}
                    </Text>
                  )}
                  {record.status === "NOT_INVITED" && (
                    <Text size="md" color="grey1">
                      {t("CONSULTANT_PROFILES.DETAIL.EMAIL.INVITATION_NOT_SEND")}
                    </Text>
                  )}
                </Flex>
                {record.pendingEmail && (
                  <Flex direction="column">
                    <Text size="md" color="grey1" wordBreak="all">
                      {record.pendingEmail}
                    </Text>
                    <Text size="md" color="grey1">
                      {t("CONSULTANT_PROFILES.DETAIL.EMAIL.WAITING_FOR_CONFIRMATION")}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Group>
            {(record.status === "NOT_INVITED" || isOwner || record.status === "PENDING") && (
              <Flex position="absolute" side="right">
                <Button
                  variant="clean"
                  onClick={() => {
                    setEditEmailModalOpen(true);
                  }}>
                  <Icon color="grey2" icon="edit" size="lg" />
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
        <EmailHandlingModal
          emailVariant="SELECT"
          variant="ASSIGN_TO_CONSULTANT"
          hasEmail
          hasPassword={record.status !== "NOT_INVITED" && record.status !== "PENDING"}
          onSubmit={assignEmailToConsultantProfile({
            companyId: User.contextCompany && User.contextCompany.id,
            profileId: record.id,
          })}
          afterSubmit={handleEmailUpdate}
          onClose={() => {
            setEditEmailModalOpen(false);
          }}
          open={isEditEmailModalOpen}
          size="xxs"
        />
      </Flex>
    );
  },
);
